import { fullScreenChartPalette, regularChartPalette } from '@/constants/constants';

import type { Theme } from '@mui/material';

import { GX_TEXT } from '@/constants/colors';

export type AppearanceMode =
  | 'regular'
  | 'fullscreen-light'
  | 'fullscreen-dark'
  | 'export'
  | 'preview';

export interface ChartAppearance {
  backgroundColor: string;
  textColor: string;
  gridColor: string;
  palette: string[];
  tooltipBackgroundColor: string;
  fontFamily: string;
  watermarkName: string;
}

const GRID_COLOR = '#333';
const FONT_FAMILY = 'Cairo, sans-serif';
const WATERMARK_BLACK = '/watermark-black.svg';
const WATERMARK_COLOR = '/watermark-color.svg';

/**
 * Creates a normalized chart appearance object based on theme and mode.
 *
 * Decision: custom JS appearance model is used instead of the Highcharts 12.3+
 * Adaptive theme class approach because the app's design palette diverges
 * significantly from Highcharts Adaptive defaults (two custom palettes, custom
 * grid/tooltip colors). CSS variable resolution was also evaluated but the
 * current token structure does not map 1:1 to Highcharts color options.
 */
export const createChartAppearance = (theme: Theme, mode: AppearanceMode): ChartAppearance => {
  if (mode === 'fullscreen-light') {
    return {
      backgroundColor: theme.palette.gx.text,
      textColor: theme.palette.gx.tileDark,
      gridColor: GRID_COLOR,
      palette: fullScreenChartPalette,
      tooltipBackgroundColor: GRID_COLOR,
      fontFamily: FONT_FAMILY,
      watermarkName: WATERMARK_COLOR
    };
  }

  // regular, fullscreen-dark, export, preview — all use the same dark/default appearance.
  return {
    backgroundColor: theme.palette.gx.tileDark,
    textColor: theme.palette.gx.text,
    gridColor: GRID_COLOR,
    palette: regularChartPalette,
    tooltipBackgroundColor: GRID_COLOR,
    fontFamily: FONT_FAMILY,
    watermarkName: WATERMARK_BLACK
  };
};

/**
 * Returns the palette color for a series at the given combined index.
 *
 * Always derives color from the palette by index — never reads series.color as
 * a source of truth. This prevents the stale-color bug caused by `??=`
 * assignment, where a color written in one mode persists after switching modes.
 *
 * @param appearance - The chart appearance object
 * @param combinedIndex - The combined index of the series in the chart
 * @param fallbackColor - Color returned when combinedIndex exceeds threshold (> 5)
 */
export const getSeriesPaletteColor = (
  appearance: ChartAppearance,
  combinedIndex: number,
  fallbackColor = GX_TEXT
): string => {
  if (combinedIndex > 5) return fallbackColor;
  const { palette } = appearance;
  return palette[combinedIndex % palette.length];
};

/**
 * Converts a ChartAppearance into Highcharts-compatible option overrides
 * for chart background, axes, and typography. Intended to be merged into
 * the base options object produced by useBaseChartOptions.
 */
export const createAppearanceOptions = (appearance: ChartAppearance) => ({
  chart: {
    backgroundColor: appearance.backgroundColor,
    style: { fontFamily: appearance.fontFamily }
  },
  xAxis: {
    labels: { style: { color: appearance.textColor } },
    gridLineColor: appearance.gridColor,
    lineColor: appearance.gridColor,
    tickColor: appearance.gridColor
  },
  yAxis: {
    labels: { style: { color: appearance.textColor } },
    gridLineColor: appearance.gridColor
  }
});
